import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex flex-col mt-6 justify-between gap-4" }
const _hoisted_2 = {
  key: 0,
  class: "flex flex-col gap-2"
}
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "text-xs font-medium text-gray-500 ml-1" }, "Situação da Vaga", -1)
const _hoisted_4 = ["title", "data-disabled"]

import { storeToRefs } from 'pinia'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { usePositionStore } from '@/pinia-store/usePositionStore'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import { isSameId, toId, type SelectId } from '@/utils/selectId'


export default /*#__PURE__*/_defineComponent({
  __name: 'MultipleSchedules',
  props: [
  'company',
  'healthUnits',
  'schedules',
  'doctors',
  'positions',
  'shifts',
  'isOpenDoctorStation',
  // Texto do tooltip; quando vem, o toggle "Situação da Vaga" fica desabilitado (chips M10-A ativos).
  'openDoctorStationDisabledHint',
  // Consulta com chips de cobertura (M10-A) disponíveis: o toggle legado não é exibido.
  'hideOpenDoctorStation'
],
  emits: [
  'update:company',
  'update:healthUnits',
  'update:schedules',
  'update:doctors',
  'update:positions',
  'update:shifts',
  'update:isOpenDoctorStation'
],
  setup(__props, { emit }) {



/* eslint-disable sonarjs/no-duplicate-string */


const healthUnitStore = useHealthUnitStore()
const scheduleStore = useScheduleStore()
const doctorStore = useDoctorStore()
const positionStore = usePositionStore()
const shiftStore = useShiftStore()

const { listCompanies } = storeToRefs(useCompanyStore())
const { listHealthUnitsByCompany } = storeToRefs(useHealthUnitStore())
const { listSchedules } = storeToRefs(useScheduleStore())
const { listDoctorsCompany } = storeToRefs(useDoctorStore())
const { listPositions } = storeToRefs(usePositionStore())
const { listShifts } = storeToRefs(useShiftStore())

const toIds = (values: SelectId[] | null | undefined) =>
  (values ?? []).map(toId).filter((id): id is number => id !== undefined)

const setCompany = (v: SelectId) => {
  const companyId = toId(v)
  emit('update:company', companyId)
  emit('update:healthUnits', undefined)
  emit('update:schedules', undefined)
  emit('update:shifts', undefined)
  emit('update:positions', undefined)
  healthUnitStore.setHealthUnitsByCompany(companyId ?? null)
  doctorStore.applyFilters({ companies: companyId ? [String(companyId)] : [] }, false)
}

const setHealthUnits = (healthUnits: SelectId[]) => {
  const healthUnitIds = toIds(healthUnits)
  emit('update:healthUnits', healthUnitIds)
  emit('update:schedules', undefined)
  emit('update:shifts', undefined)
  emit('update:positions', undefined)
  scheduleStore.setSchedulesByHealthUnits(healthUnitIds)
}

const setSchedules = async (schedules: SelectId[]) => {
  const scheduleIds = toIds(schedules)
  emit('update:schedules', scheduleIds)
  emit('update:positions', undefined)
  emit('update:shifts', undefined)

  await Promise.all([
    positionStore.setPositionsBySchedules(scheduleIds),
    shiftStore.setShiftsBySchedules(scheduleIds)
  ])
}

const setIsOpenDoctorStation = (value: any) => {
  const realValue = value === 'all' ? undefined : value
  emit('update:isOpenDoctorStation', realValue)

  if (realValue === true) {
    emit('update:doctors', undefined)
  }
}

const setShifts = (shifts: string[]) => {
  emit('update:shifts', shifts)
}

const setDoctors = (doctors: string[]) => {
  emit('update:doctors', doctors)
}

const setPositions = (positions: string[]) => {
  emit('update:positions', positions)
}

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_btn_toggle = _resolveComponent("v-btn-toggle")!
  const _component_v_combobox = _resolveComponent("v-combobox")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_autocomplete, {
      "onUpdate:modelValue": setCompany,
      "model-value": __props.company ?? undefined,
      class: "w-full",
      color: "primary20",
      label: "Empresa",
      theme: "light",
      items: _unref(listCompanies),
      "item-title": "title",
      "item-value": "value",
      "value-comparator": _unref(isSameId),
      variant: "outlined",
      clearable: "",
      density: "compact",
      "hide-details": "auto"
    }, null, 8, ["model-value", "items", "value-comparator"]),
    _createVNode(_component_v_autocomplete, {
      "onUpdate:modelValue": setHealthUnits,
      "model-value": __props.healthUnits ?? undefined,
      class: "w-full",
      color: "primary20",
      label: "Unidades",
      items: _unref(listHealthUnitsByCompany),
      "item-title": "title",
      "item-value": "value",
      "value-comparator": _unref(isSameId),
      variant: "outlined",
      density: "compact",
      chips: "",
      multiple: "",
      clearable: "",
      disabled: !__props.company,
      "hide-details": "auto"
    }, null, 8, ["model-value", "items", "value-comparator", "disabled"]),
    _createVNode(_component_v_autocomplete, {
      "onUpdate:modelValue": setSchedules,
      "model-value": __props.schedules ?? undefined,
      class: "w-full",
      color: "primary20",
      label: "Escalas",
      theme: "light",
      items: _unref(listSchedules),
      "item-title": "title",
      "item-value": "value",
      "value-comparator": _unref(isSameId),
      variant: "outlined",
      density: "compact",
      chips: "",
      multiple: "",
      clearable: "",
      disabled: !__props.healthUnits?.length,
      "hide-details": "auto"
    }, {
      item: _withCtx(({ props, item }) => [
        _createVNode(_component_v_list_item, _mergeProps(props, {
          title: item?.raw?.title,
          subtitle: item?.raw?.subtitle
        }), null, 16, ["title", "subtitle"])
      ]),
      _: 1
    }, 8, ["model-value", "items", "value-comparator", "disabled"]),
    _createVNode(_component_v_autocomplete, {
      "onUpdate:modelValue": setShifts,
      "model-value": __props.shifts ? __props.shifts : undefined,
      class: "w-full",
      color: "primary20",
      label: "Turnos",
      theme: "light",
      items: _unref(listShifts),
      "item-title": "name",
      "item-value": "id",
      variant: "outlined",
      density: "compact",
      chips: "",
      multiple: "",
      clearable: "",
      disabled: !__props.schedules?.length,
      "hide-details": "auto"
    }, {
      item: _withCtx(({ props, item }) => [
        _createVNode(_component_v_list_item, _mergeProps(props, {
          title: item?.raw?.name
        }), null, 16, ["title"])
      ]),
      chip: _withCtx(({ props, item }) => [
        _createVNode(_component_v_chip, _mergeProps({ color: "gray" }, props, {
          text: item.raw.name
        }), null, 16, ["text"])
      ]),
      _: 1
    }, 8, ["model-value", "items", "disabled"]),
    _createVNode(_component_v_autocomplete, {
      "onUpdate:modelValue": setPositions,
      "model-value": __props.positions ? __props.positions : undefined,
      class: "w-full",
      color: "primary20",
      label: "Posições",
      theme: "light",
      items: _unref(listPositions),
      "item-title": "name",
      "item-value": "id",
      variant: "outlined",
      density: "compact",
      chips: "",
      multiple: "",
      clearable: "",
      disabled: !__props.schedules?.length,
      "hide-details": "auto"
    }, {
      item: _withCtx(({ props, item }) => [
        _createVNode(_component_v_list_item, _mergeProps(props, {
          title: item?.raw?.name,
          subtitle: item?.raw?.schedule?.name
        }), null, 16, ["title", "subtitle"])
      ]),
      chip: _withCtx(({ props, item }) => [
        _createVNode(_component_v_chip, _mergeProps({ color: "gray" }, props, {
          text: item.raw.name
        }), null, 16, ["text"])
      ]),
      _: 1
    }, 8, ["model-value", "items", "disabled"]),
    (!__props.hideOpenDoctorStation)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _hoisted_3,
          _createElementVNode("div", {
            title: __props.openDoctorStationDisabledHint || undefined,
            "data-testid": "open-doctor-station-toggle",
            "data-disabled": !!__props.openDoctorStationDisabledHint
          }, [
            _createVNode(_component_v_btn_toggle, {
              disabled: !!__props.openDoctorStationDisabledHint,
              "model-value": __props.isOpenDoctorStation === undefined ? 'all' : __props.isOpenDoctorStation,
              "onUpdate:modelValue": setIsOpenDoctorStation,
              color: "primary20",
              variant: "outlined",
              divided: "",
              density: "compact",
              class: "w-full max-w-full"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_btn, {
                  value: "all",
                  class: "flex-1 !text-xs md:!text-sm !px-2 md:!px-4"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Todas")
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  value: true,
                  class: "flex-1 !text-xs md:!text-sm !px-2 md:!px-4"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Abertas")
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_btn, {
                  value: false,
                  class: "flex-1 !text-xs md:!text-sm !px-2 md:!px-4"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Preenchidas")
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["disabled", "model-value"])
          ], 8, _hoisted_4)
        ]))
      : _createCommentVNode("", true),
    _createVNode(_component_v_combobox, {
      "onUpdate:modelValue": setDoctors,
      "model-value": __props.doctors ? __props.doctors : undefined,
      items: _unref(listDoctorsCompany),
      multiple: "",
      chips: "",
      clearable: "",
      label: "Médicos",
      class: "w-full",
      color: "primary20",
      variant: "outlined",
      density: "compact",
      disabled: !__props.company || __props.isOpenDoctorStation === true,
      "item-title": "title",
      "item-value": "value",
      "return-object": false,
      "hide-details": "auto",
      hint: __props.isOpenDoctorStation === true ? 'Desabilitado: Buscando apenas vagas abertas' : '',
      "persistent-hint": ""
    }, {
      item: _withCtx(({ props, item }) => [
        _createVNode(_component_v_list_item, _mergeProps(props, {
          title: item.raw.title,
          subtitle: item.raw.subtitle
        }), null, 16, ["title", "subtitle"])
      ]),
      _: 1
    }, 8, ["model-value", "items", "disabled", "hint"])
  ]))
}
}

})